import { chladni, type ChladniParamsWithSize } from "@ui/lib/chladni";
import { useId } from "react";
import { optimizeSVGPath } from "./optimizeSVGPath";

export function ChladniSVG({
  params,
  ...props
}: React.ComponentPropsWithRef<"svg"> & { params: ChladniParamsWithSize }) {
  const id = useId();

  return (
    <svg
      viewBox={`0 0 ${params.width} ${params.height}`}
      fill='none'
      stroke='currentColor'
      aria-hidden
      dangerouslySetInnerHTML={{
        __html: toSVG({ id, ...params }),
      }}
      {...props}
    />
  );
}

// Minify numbers by rounding and removing non-significant zeros, e.g.
// 0.10 → .1
// -0.87 → -.87
function round(number: number) {
  let r = number.toFixed(2);
  if (r === "0.00" || r === "-0.00") {
    return "0";
  }
  if (r.startsWith("0")) {
    r = r.slice(1);
  } else if (r.startsWith("-0")) {
    r = `-${r.slice(2)}`;
  }
  return r.replace(/0+$/, "").replace(/\.$/, "");
}

export function toSVG({
  id,
  ...params
}: ChladniParamsWithSize & {
  id: string;
}) {
  const { paths, symmetry } = chladni(params);

  let path = "";

  for (let i = 0; i < paths.length; i++) {
    const points = paths[i];
    for (let j = 0; j < points.length; j++) {
      const point = points[j];
      path += j === 0 ? "M" : "L";
      path += `${point.x} ${point.y}`;
    }
  }

  let svg = `<path id="${id}" d="${optimizeSVGPath(path)}" vector-effect="non-scaling-stroke" />`;

  const matrices =
    symmetry === "quadrant"
      ? [
          [-1, 0, 0, 1, params.width, 0],
          [1, 0, 0, -1, 0, params.height],
          [-1, 0, 0, -1, params.width, params.height],
        ]
      : symmetry === "bilateral"
        ? [[-1, 0, 0, 1, params.width, 0]]
        : [];

  for (const matrix of matrices) {
    const transform = `matrix(${matrix.join(" ")})`;
    svg += `<use href="#${id}" transform="${transform}"/>`;
  }

  return svg;
}
